import { useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getChannelConfigs,
  deleteChannelConfig,
  testSendChannelConfig,
  type NotificationChannelConfig,
  type ChannelConfigFilters,
} from '@/api/notificationApi';
import { useAuth } from '@/hooks/useAuth';
import AdminNav from './AdminNav';

const CHANNEL_TYPES = ['email', 'sms'];

export default function ChannelConfigListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [channelTypeFilter, setChannelTypeFilter] = useState<string | undefined>();

  const canRead = hasPermission('admin', 'read');
  const canWrite = hasPermission('admin', 'write');
  const canDelete = hasPermission('admin', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: ChannelConfigFilters = { ...params };
      if (channelTypeFilter) filters.channel_type = channelTypeFilter;
      return getChannelConfigs(filters);
    },
    [channelTypeFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('notification.confirm_delete_config'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteChannelConfig(id);
            message.success(t('notification.config_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleTestSend = useCallback(
    async (id: string) => {
      try {
        const result = await testSendChannelConfig(id);
        if (result.success) {
          message.success(t('notification.test_send_success'));
        } else {
          message.error(t('notification.test_send_failed'));
        }
      } catch {
        message.error(t('notification.test_send_failed'));
      }
    },
    [t]
  );

  const columns: ColumnsType<NotificationChannelConfig> = useMemo(
    () => [
      {
        title: t('notification.config_field.channel_type'),
        dataIndex: 'channel_type',
        key: 'channel_type',
        width: 120,
        render: (ch: string) => (
          <Tag>{t(`notification.channel.${ch}`, ch)}</Tag>
        ),
      },
      {
        title: t('notification.config_field.provider'),
        dataIndex: 'provider',
        key: 'provider',
        width: 120,
        render: (p: string) => (
          <Tag color="blue">{t(`notification.provider.${p}`, p)}</Tag>
        ),
      },
      {
        title: t('notification.config_field.is_active'),
        dataIndex: 'is_active',
        key: 'is_active',
        width: 80,
        render: (v: boolean) => (
          <Tag color={v ? 'green' : 'default'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('notification.config_field.priority'),
        dataIndex: 'priority',
        key: 'priority',
        width: 80,
      },
      {
        title: t('notification.config_field.max_retries'),
        dataIndex: 'max_retries',
        key: 'max_retries',
        width: 100,
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 160,
        render: (_: unknown, record: NotificationChannelConfig) => (
          <Space size="small">
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<SendOutlined />}
                onClick={() => handleTestSend(record.id)}
                title={t('notification.test_send')}
              />
            )}
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/admin/channel-configs/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete, handleTestSend]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('notification.config_field.channel_type')}
        value={channelTypeFilter}
        onChange={(v) => { setChannelTypeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 160 }}
        options={CHANNEL_TYPES.map((ch) => ({
          value: ch,
          label: t(`notification.channel.${ch}`),
        }))}
      />
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/channel-configs/new')}
        >
          {t('notification.create_config_title')}
        </Button>
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <AdminNav />
      <h2>{t('notification.channel_configs_title')}</h2>
      <DataGrid<NotificationChannelConfig>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('notification.search_configs_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
